
import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAdminReports } from "@/hooks/useAdminReports";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AdminContent } from "@/components/admin/AdminContent";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Tag, Inbox } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const AdminDashboard: React.FC = () => {
  const {
    reports,
    isLoading,
    currentPage,
    totalPages,
    handlePageChange,
    handleReportUpdate,
    search,
    setSearch,
    onSearch,
    showOtherSubjectsOnly,
    setShowOtherSubjectsOnly,
    sortField,
    sortDirection,
    handleSort,
    statusFilter,
    setStatusFilter,
    learningStatusFilter,
    setLearningStatusFilter,
    submittedOnTimeFilter,
    setSubmittedOnTimeFilter,
    hoursDeductedFilter,
    setHoursDeductedFilter,
    teachingDateFrom,
    setTeachingDateFrom,
    teachingDateTo,
    setTeachingDateTo,
    resetFilters,
    hasActiveFilters,
  } = useAdminReports();

  // Count PENDING TEACHER_REQUEST เพื่อแสดงบนปุ่ม
  const { data: pendingCount = 0 } = useQuery({
    queryKey: ["pending-edit-requests-count"],
    queryFn: async () => {
      const { count, error } = await (supabase as any)
        .from("report_change_logs")
        .select("id", { count: "exact", head: true })
        .eq("status", "PENDING")
        .eq("type", "TEACHER_REQUEST");
      if (error) {
        console.warn("Could not load pending count:", error.message);
        return 0;
      }
      return count ?? 0;
    },
    refetchInterval: 30000, // refetch ทุก 30 วินาที
  });

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <SidebarInset>
          <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <h1 className="text-2xl font-bold">รายงานพัฒนาการนักเรียน</h1>
              <Button variant="outline" asChild className="w-fit">
                <Link to="/admin/report-edit-requests">
                  <Inbox className="h-4 w-4 mr-2" />
                  คำขอแก้ไขรายงาน
                  {pendingCount > 0 && (
                    <Badge variant="destructive" className="ml-2">
                      {pendingCount}
                    </Badge>
                  )}
                </Link>
              </Button>
            </div>

            <Alert className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                คุณสามารถค้นหา และกรองรายงานได้ นอกจากนี้ยังสามารถกรองเพื่อดูเฉพาะรายงานที่มีหัวข้ออื่นๆ
                <span className="font-medium ml-1">
                  โดยหัวข้อที่เป็น "หัวข้ออื่นๆ" จะมี <Tag className="inline h-3 w-3 text-amber-600" /> แท็กกำกับไว้
                </span>
              </AlertDescription>
            </Alert>
            
            <AdminContent
              isLoading={isLoading}
              reports={reports}
              currentPage={currentPage}
              totalPages={totalPages}
              onReportUpdate={handleReportUpdate}
              onPageChange={handlePageChange}
              search={search}
              setSearch={setSearch}
              onSearch={onSearch}
              showOtherSubjectsOnly={showOtherSubjectsOnly}
              setShowOtherSubjectsOnly={setShowOtherSubjectsOnly}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              learningStatusFilter={learningStatusFilter}
              setLearningStatusFilter={setLearningStatusFilter}
              submittedOnTimeFilter={submittedOnTimeFilter}
              setSubmittedOnTimeFilter={setSubmittedOnTimeFilter}
              hoursDeductedFilter={hoursDeductedFilter}
              setHoursDeductedFilter={setHoursDeductedFilter}
              teachingDateFrom={teachingDateFrom}
              setTeachingDateFrom={setTeachingDateFrom}
              teachingDateTo={teachingDateTo}
              setTeachingDateTo={setTeachingDateTo}
              resetFilters={resetFilters}
              hasActiveFilters={hasActiveFilters}
            />
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default AdminDashboard;
